import AbstractListView from '@UI/Store/AbstractListView';
import type StaffMemberView from '@UI/Store/StaffMembers/Views/StaffMemberView';

/** Mirrors `apps/backend/src/enums/staff/staff-function.enum.ts`: no `StaffFunction` enum exists on the front. */
const NURSING_STAFF_JOB_FUNCTION = 'nursing_staff';
const HOME_CARE_JOB_FUNCTION = 'home_care_worker';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor. Carries
 * `organizationId` alone as its own scope — no `totalRecords`, no `queryKey`: the collection arrives
 * entire (`fetchStaffMembers` walks every page) and every filter is applied client-side, so there is
 * neither server pagination nor a server filter perimeter to track.
 *
 * `facilityId` records whether `forFacility` restricted this instance (`null` = the whole
 * organization). Without it, a restricted instance would carry the same `organizationId` as the full
 * one and be indistinguishable from it — a risk for any `stopOn` comparing only `organizationId`.
 */
class StaffMembersListView extends AbstractListView<StaffMemberView> {
	readonly organizationId: number;
	readonly facilityId: number | null;

	constructor(list: readonly StaffMemberView[], organizationId: number, facilityId: number | null = null) {
		super(list, 'StaffMembersListView');

		this.organizationId = organizationId;
		this.facilityId = facilityId;

		Object.freeze(this);
	}

	/** Restricts the list to members assigned to `facilityId` (`facilityIds` may hold several agencies). */
	forFacility(facilityId: number): StaffMembersListView {
		return new StaffMembersListView(
			this.list.filter((member) => member.facilityIds.includes(facilityId)),
			this.organizationId,
			facilityId,
		);
	}

	nursingStaff(): readonly StaffMemberView[] {
		return this.list.filter((member) => member.jobFunction.value === NURSING_STAFF_JOB_FUNCTION);
	}

	homeCareStaff(): readonly StaffMemberView[] {
		return this.list.filter((member) => member.jobFunction.value === HOME_CARE_JOB_FUNCTION);
	}

	addStaffMember(staffMember: StaffMemberView): StaffMembersListView {
		return new StaffMembersListView([...this.list, staffMember], this.organizationId, this.facilityId);
	}

	updateStaffMember(staffMember: StaffMemberView): StaffMembersListView {
		return new StaffMembersListView(
			this.list.map((entry) => (entry.id === staffMember.id ? staffMember : entry)),
			this.organizationId,
			this.facilityId,
		);
	}

	removeStaffMember(staffMemberId: number): StaffMembersListView {
		return new StaffMembersListView(
			this.list.filter((entry) => entry.id !== staffMemberId),
			this.organizationId,
			this.facilityId,
		);
	}
}

export default StaffMembersListView;
